import { initializeApp } from '@firebase/app';
import { equalTo, get as dbGet, orderByChild, query as dbQuery, ref, set, push, remove, update } from "firebase/database";
import { Timestamp } from '@firebase/firestore';
import { getAuth, onAuthStateChanged as onAuthStateChangedFn, signInWithEmailAndPassword, sendPasswordResetEmail, signOut, updateEmail, updatePassword, sendSignInLinkToEmail } from '@firebase/auth';
import { getDatabase } from "firebase/database";
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL, listAll, deleteObject } from "firebase/storage";
import { ROLES } from '../components/misc/Utils';

var config = {
    apiKey: process.env.REACT_APP_API_KEY,
    authDomain: process.env.REACT_APP_AUTH_DOMAIN,
    databaseURL: process.env.REACT_APP_DATABASE_URL,
    projectId: process.env.REACT_APP_PROJECT_ID,
    storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
};

export default class FirebaseNewImproved {
    constructor() {
        this.app = initializeApp(config);
        this.db = getDatabase(this.app)
        this.auth = getAuth(this.app)
        this.storage = getStorage(this.app)
    }

    // --- Authentication Methods ---

    onAuthStateChanged = (callback) => {
        return onAuthStateChangedFn(this.auth, callback);
    }

    doSignInWithEmailAndPassword = (email, password) => {
        return signInWithEmailAndPassword(this.auth, email, password);
    }

    doSignOut = () => signOut(this.auth)

    passwordForgot = async (email) => {
        let result = { success: false, reason: [] };
        const CODES = {
            "auth/user-not-found": 'Invalid Email',
            "auth/missing-email": 'Missing Email',
            "auth/invalid-email": 'Invalid Email'
        };
        try {
            await sendPasswordResetEmail(this.auth, email);
            result.success = true;
        } catch (e) {
            result.reason = result.reason.concat(CODES[e.code] || 'An unknown error occurred please try again later');
        } finally {
            return result;
        }
    }

    updateUserEmail = async (newEmail) => {
        let result = { success: false, reason: [] };
        const CODES = { "auth/invalid-email": 'Email format is invalid' };
        try {
            await updateEmail(this.auth.currentUser, newEmail);
            result.success = true;
            result.reason = result.reason.concat('Successfully Changed Email');
        } catch (e) {
            console.error('updateUserEmail error:', e);
            result.reason = result.reason.concat(CODES[e.code] || 'An unknown error occurred please try again later');
        } finally {
            return result;
        }
    }

    updateUserPassword = async (newPassword) => {
        const CODES = { "auth/requires-recent-login": 'Please Login Again Before Attempting To Change Your Password' };
        let result = { success: false, reason: [] };
        try {
            await updatePassword(this.auth.currentUser, newPassword);
            result.success = true;
            result.reason = result.reason.concat('Successfully Changed Password');
        } catch (e) {
            console.error('updateUserPassword error:', e);
            result.reason = result.reason.concat(CODES[e.code] || 'An unknown error occurred please try again later');
        } finally {
            return result;
        }
    }

    now = () => Timestamp.now().toMillis();

    // --- User Methods ---
    User = (data) => ({
        uid: data.uid || '',
        email: data.email || '',
        username: data.username || '',
        role: data.role || '',
        eventsNew: data.eventsNew || {},
        teamsNew: data.teamsNew || {},
        competitions: data.competitions || {},
        defaultCompetition: data.defaultCompetition || '',
        createdAt: data.createdAt || this.now(),
        updatedAt: this.now(),
        invited: data.invited || false,
        pendingInvites: data.pendingInvites || {},
        scoresheetNew: !!data.scoresheetNew
    })

    insertUser = async (uid, data) => {
        const toSave = this.User(data);
        const location = ref(this.db, `users/${uid}`);
        console.log(`Inserting user ${uid}`);
        return set(location, toSave);
    }

    listenUser = (uid) => ref(this.db, `users/${uid}`);

    getUser = async (uid) => {
        const users = await dbGet(ref(this.db, `users/${uid}`));
        return users.val() || {};
    }

    listenUsersMap = () => ref(this.db, 'users');

    getUsers = async () => {
        const users = await dbGet(ref(this.db, 'users'));
        return users.val() || {};
    }

    bulkSetCompetitionForUsers = async (competitionKey) => {
        if (!competitionKey) throw new Error('competitionKey required');
        const snap = await dbGet(ref(this.db, 'users'));
        const usersObj = snap.val() || {};
        const updates = {};
        const targetRoles = new Set([ROLES.TEAM, ROLES.JUDGE, ROLES.SCORECOMMITTEE]);
        Object.entries(usersObj).forEach(([uid, u]) => {
            if (u && targetRoles.has(u.role)) {
                updates[`users/${uid}/defaultCompetition`] = competitionKey;
            }
        });
        const count = Object.keys(updates).length;
        if (count === 0) return 0;
        await update(ref(this.db, '/'), updates);
        return count;
    }

    patchUser = async (userKey, newUser, competition) => {
        try {
            console.log('patchUser called', userKey, competition, newUser);
            const toSave = this.User(newUser);

            if (toSave.role === 'JUDGE') {
                await this.removeUserFromTeams(userKey, toSave, competition);
                await this.addJudgeToEvents(userKey, toSave, competition);
                delete toSave.teamsNew;
            }

            if (toSave.role === 'TEAM') {
                await this.removeJudgeFromEvents(userKey, toSave, competition);
                await this.addUserToTeams(userKey, toSave, competition);
            }

            const location = ref(this.db, `users/${userKey}`);
            await set(location, toSave);
            console.log(`Patched user ${userKey}`);
            return true;
        } catch (error) {
            console.error(`Error patching user ${userKey}:`, error);
            throw error;
        }
    }


    // --- Invite and Pending Invite Methods ---

    // Invite an existing user by uid: create a pendingInvite entry (does not add to teamMembers until accepted)
    inviteExistingUser = async (userKey, competition, teamIds = [], inviterUid = '') => {
        if (!userKey || !competition) throw new Error('userKey and competition required');
        try {
            const userSnap = await dbGet(ref(this.db, `users/${userKey}`));
            const user = userSnap.val() || {};
            // Defensive: if user already has a team in the competition, do not invite
            const existingTeams = (user.teamsNew && user.teamsNew[competition]) || [];
            if (existingTeams && Object.keys(existingTeams).length > 0) {
                throw new Error('User already belongs to a team in this competition');
            }

            const pending = (user.pendingInvites) ? user.pendingInvites : {};
            pending[competition] = {
                teams: teamIds,
                invitedBy: inviterUid,
                createdAt: this.now()
            };

            const toSave = Object.assign({}, user, { pendingInvites: pending, invited: true });
            await set(ref(this.db, `users/${userKey}`), this.User(toSave));
            console.log(`Created pending invite for user ${userKey} in competition ${competition}`);
            return true;
        } catch (e) {
            console.error('inviteExistingUser error', e.message || e);
            throw e;
        }
    }

    // Send a sign-in invite email (magic link) to an email address
    sendInviteEmail = async (email, continueUrl) => {
        if (!email || !continueUrl) throw new Error('email and continueUrl required');
        try {
            const actionCodeSettings = {
                url: continueUrl,
                handleCodeInApp: true
            };
            await sendSignInLinkToEmail(this.auth, email, actionCodeSettings);
            console.log(`Sent sign-in link to ${email}`);
            return true;
        } catch (e) {
            console.error('sendInviteEmail error', e.message || e);
            throw e;
        }
    }

    // Accept a pending invite: move pendingInvites[competition] into teamsNew[competition] and add to teams
    acceptInvite = async (userKey, competition) => {
        if (!userKey || !competition) throw new Error('userKey and competition required');
        try {
            const userSnap = await dbGet(ref(this.db, `users/${userKey}`));
            const user = userSnap.val() || {};
            const pending = (user.pendingInvites && user.pendingInvites[competition]) ? user.pendingInvites[competition] : null;
            if (!pending) throw new Error('No pending invite found');

            const existingTeams = (user.teamsNew && user.teamsNew[competition]) || [];
            if (existingTeams && Object.keys(existingTeams).length > 0) {
                throw new Error('User already belongs to a team in this competition');
            }

            const toSave = Object.assign({}, user);
            toSave.teamsNew = toSave.teamsNew || {};
            // we store array of team ids under competition
            toSave.teamsNew[competition] = pending.teams || [];

            // remove pending invite for competition
            if (toSave.pendingInvites) {
                delete toSave.pendingInvites[competition];
            }

            // mark invited false since they accepted
            toSave.invited = false;

            // patch user which will call addUserToTeams (so teams are updated)
            await this.patchUser(userKey, toSave, competition);
            console.log(`User ${userKey} accepted invite for competition ${competition}`);
            return true;
        } catch (e) {
            console.error('acceptInvite error', e.message || e);
            throw e;
        }
    }

    // Decline a pending invite
    declineInvite = async (userKey, competition) => {
        if (!userKey || !competition) throw new Error('userKey and competition required');
        try {
            const userSnap = await dbGet(ref(this.db, `users/${userKey}`));
            const user = userSnap.val() || {};
            if (user.pendingInvites && user.pendingInvites[competition]) {
                delete user.pendingInvites[competition];
            }
            // if no other pending invites, clear invited flag
            const hasAny = user.pendingInvites && Object.keys(user.pendingInvites).length > 0;
            const toSave = Object.assign({}, user, { invited: hasAny });
            await set(ref(this.db, `users/${userKey}`), this.User(toSave));
            console.log(`User ${userKey} declined invite for competition ${competition}`);
            return true;
        } catch (e) {
            console.error('declineInvite error', e.message || e);
            throw e;
        }
    }


    // --- Team and Event Relationship Management ---

    // Remove user from all teams and associated events synchronously with logging
    removeUserFromTeams = async (userKey, user, competition) => {
        try {
            const teams = Object.values(user.teamsNew[competition] || {});
            console.log(`Removing user ${userKey} from teams:`, teams);

            for (const team of teams) {
                const teamMemberLocation = ref(this.db, `teamsNew/${competition}/${team}/teamMembers/${userKey}`);
                await remove(teamMemberLocation);
                console.log(`Removed user ${userKey} from teamMembers of team ${team}`);
            }

            const events = await this.getEventsByCompetition(competition);
            const eventKeys = Object.keys(events);

            for (const key of eventKeys) {
                const associatedTeams = (await dbGet(ref(this.db, `eventsNew/${competition}/${key}/associatedTeams`))).val();
                if (!associatedTeams) continue;

                for (let teamIndex = 0; teamIndex < associatedTeams.length; teamIndex++) {
                    const team = associatedTeams[teamIndex];
                    if (teams.includes(team.uid)) {
                        if (!team.teamMembers) continue;
                        for (let userIndex = 0; userIndex < team.teamMembers.length; userIndex++) {
                            if (team.teamMembers[userIndex] === userKey) {
                                const eventTeamMemberLocation = ref(this.db, `eventsNew/${competition}/${key}/associatedTeams/${teamIndex}/teamMembers/${userIndex}`);
                                await remove(eventTeamMemberLocation);
                                console.log(`Removed user ${userKey} from event ${key} associatedTeams[${teamIndex}] teamMembers`);
                            }
                        }
                    }
                }
            }

            console.log(`Completed removal of user ${userKey} from all teams and associated events.`);
        } catch (error) {
            console.error(`Error removing user ${userKey} from teams and events:`, error);
            throw error;
        }
    }

    // Add user to all teams and associated events synchronously with logging
    addUserToTeams = async (userKey, user, competition) => {
        try {
            const teams = Object.values(user.teamsNew[competition] || {});
            console.log(`Adding user ${userKey} to teams:`, teams);

            for (const team of teams) {
                const teamMemberLocation = ref(this.db, `teamsNew/${competition}/${team}/teamMembers/${userKey}`);
                await set(teamMemberLocation, user);
                console.log(`Added user ${userKey} to teamMembers of team ${team}`);
            }

            const events = await this.getEventsByCompetition(competition);
            const eventKeys = Object.keys(events);

            for (const key of eventKeys) {
                const associatedTeams = (await dbGet(ref(this.db, `eventsNew/${competition}/${key}/associatedTeams`))).val();
                if (!associatedTeams) continue;

                for (let teamIndex = 0; teamIndex < associatedTeams.length; teamIndex++) {
                    const team = associatedTeams[teamIndex];
                    if (teams.includes(team.uid)) {
                        if (!team.teamMembers) {
                            team.teamMembers = [];
                        }
                        if (!team.teamMembers.includes(userKey)) {
                            team.teamMembers.push(userKey);
                            const eventTeamMembersLocation = ref(this.db, `eventsNew/${competition}/${key}/associatedTeams/${teamIndex}/teamMembers`);
                            await set(eventTeamMembersLocation, team.teamMembers);
                            console.log(`Added user ${userKey} to event ${key} associatedTeams[${teamIndex}] teamMembers`);
                        }
                    }
                }
            }

            console.log(`Completed adding user ${userKey} to all teams and associated events.`);
        } catch (error) {
            console.error(`Error adding user ${userKey} to teams and events:`, error);
            throw error;
        }
    }

    // --- Judge Methods ---

    Judge = (data) => ({
        uid: data.uid || '',
        company: data.company || '',
        email: data.email || '',
        createdAt: data.createdAt || this.now(),
        updatedAt: this.now()
    })

    insertJudge = async (judgeKey, data) => {
        const toSave = this.Judge(data);
        const location = ref(this.db, `judgesNew/${judgeKey}`);
        console.log(`Inserting judge ${judgeKey}`);
        return set(location, toSave);
    }

    listenJudgesMap = () => {
        const queryConstraints = [orderByChild('role'), equalTo(ROLES.JUDGE)];
        return dbQuery(ref(this.db, 'users'), ...queryConstraints);
    }

    getJudgesMap = async () => {
        const queryConstraints = [orderByChild('role'), equalTo(ROLES.JUDGE)];
        const judgeUsers = await dbGet(dbQuery(ref(this.db, 'users'), ...queryConstraints));
        return judgeUsers.val() || {};
    }

    patchJudge = async (judgeKey, newJudge) => {
        try {
            const toSave = this.Judge(newJudge);
            const location = ref(this.db, `judgesNew/${judgeKey}`);
            await set(location, toSave);
            console.log(`Patched judge ${judgeKey}`);
        } catch (error) {
            console.error(`Error patching judge ${judgeKey}:`, error);
            throw error;
        }
    }

    // Remove judge from all events synchronously with logging
    removeJudgeFromEvents = async (judgeKey, user, competition) => {
        try {
            const events = Object.values(user.eventsNew[competition] || {});
            console.log(`Removing judge ${judgeKey} from events:`, events);

            for (const event of events) {
                const eventJudgeLocation = ref(this.db, `eventsNew/${competition}/${event}/judgesNew/${judgeKey}`);
                await remove(eventJudgeLocation);
                console.log(`Removed judge ${judgeKey} from event ${event}`);
            }

            console.log(`Completed removal of judge ${judgeKey} from all events.`);
        } catch (error) {
            console.error(`Error removing judge ${judgeKey} from events:`, error);
            throw error;
        }
    }

    // Add judge to all events synchronously with logging
    addJudgeToEvents = async (judgeKey, user, competition) => {
        try {
            const events = Object.values(user.eventsNew[competition] || {});
            console.log(`Adding judge ${judgeKey} to events:`, events);

            for (const event of events) {
                const eventJudgeLocation = ref(this.db, `eventsNew/${competition}/${event}/judgesNew/${judgeKey}`);
                await set(eventJudgeLocation, user);
                console.log(`Added judge ${judgeKey} to event ${event}`);
            }

            console.log(`Completed adding judge ${judgeKey} to all events.`);
        } catch (error) {
            console.error(`Error adding judge ${judgeKey} to events:`, error);
            throw error;
        }
    }

    // --- Team Methods ---

    Team = (data) => ({
        category: data.category || '',
        division: data.division || '',
        teamNumber: data.teamNumber || '',
        teamName: data.teamName || '',
        teamMembers: data.teamMembers || [],
        associatedEvents: data.associatedEvents || [],
        sponsors: data.sponsors || [],
        checkedIn: data.checkedIn || false,
        createdAt: data.createdAt || this.now(),
        updatedAt: this.now()
    })

    insertTeam = async (team, competition) => {
        if (!competition) {
            console.warn('insertTeam called without competition');
            return {};
        }
        const toSave = this.Team(team);
        const location = ref(this.db, `teamsNew/${competition}`);
        console.log(`Inserting team in competition ${competition}`);
        return push(location, toSave);
    }

    listenTeamsMap = () => ref(this.db, 'teamsNew');

    getTeamsMap = async () => {
        const teams = await dbGet(ref(this.db, 'teamsNew'));
        return teams.val() || {};
    }

    getTeam = async (teamKey, competition) => {
        if (!teamKey || !competition) {
            console.warn('getTeam called without teamKey or competition');
            return false;
        }
        const team = await dbGet(ref(this.db, `teamsNew/${competition}/${teamKey}`));
        return team.val() || {};
    }

    patchTeam = async (teamKey, newTeam, competition) => {
        try {
            // support compound key format "competition/teamKey"
            let comp = competition;
            let key = teamKey;
            if (!comp && typeof teamKey === 'string' && teamKey.includes('/')) {
                const parts = teamKey.split('/');
                comp = parts[0];
                key = parts.slice(1).join('/');
            }
            const toSave = this.Team(newTeam);
            const location = ref(this.db, `teamsNew/${comp}/${key}`);
            await set(location, toSave);
            console.log(`Patched team ${key} in competition ${comp}`);
        } catch (error) {
            console.error(`Error patching team ${teamKey} in competition ${competition}:`, error);
            throw error;
        }
    }

    deleteTeam = async (teamKey, competition, members) => {
        if (!teamKey || !competition) {
            console.warn('deleteTeam called without teamKey or competition');
            return false;
        }
        try {
            for (const mem of members) {
                const memberLocation = ref(this.db, `users/${mem}/teamsNew/${competition}`);
                const snapshot = await dbGet(memberLocation);
                if (snapshot.exists()) {
                    snapshot.forEach((child) => {
                        if (child.val() === teamKey) {
                            const memLocation = ref(this.db, `users/${mem}/teamsNew/${competition}/${child.key}`);
                            remove(memLocation);
                            console.log(`Removed team ${teamKey} from user ${mem} teamsNew`);
                        }
                    });
                } else {
                    console.log(`No teamsNew data found for user ${mem} in competition ${competition}`);
                }
            }
            const location = ref(this.db, `teamsNew/${competition}/${teamKey}`);
            await remove(location);
            console.log(`Deleted team ${teamKey} from competition ${competition}`);
        } catch (error) {
            console.error(`Error deleting team ${teamKey} from competition ${competition}:`, error);
            throw error;
        }
    }

    // --- Event Methods ---

    Event = (data) => ({
        name: data.name || '',
        number: parseInt(data.number) || '',
        judgesNew: data.judgesNew || [],
        isQuiz: !!data.isQuiz,
        createdAt: data.createdAt || this.now(),
        updatedAt: this.now(),
        excludeEvent: data.excludeEvent || false,
        company: data.company || '',
        fullTitle: data.fullTitle || '',
        associatedTeams: data.associatedTeams || []
    })

    insertEvent = async (event, competition) => {
        const toSave = this.Event(event);
        const location = ref(this.db, `eventsNew/${competition}`);
        console.log(`Inserting event in competition ${competition}`);
        return push(location, toSave);
    }

    listenEventsMap = () => ref(this.db, `eventsNew`);

    getEventsByCompetition = async (competition) => {
        const events = await dbGet(ref(this.db, `eventsNew/${competition}`));
        return events.val() || {};
    }

    getEvent = async (eventKey, competition) => {
        if (!eventKey || !competition) {
            console.warn('getEvent called without eventKey or competition');
            return false;
        }
        const event = await dbGet(ref(this.db, `eventsNew/${competition}/${eventKey}`));
        return event.val() || {};
    }

    patchEvent = async (eventKey, newEvent, competition) => {
        try {
            const toSave = this.Event(newEvent);
            const location = ref(this.db, `eventsNew/${competition}/${eventKey}`);
            await set(location, toSave);
            console.log(`Patched event ${eventKey} in competition ${competition}`);
        } catch (error) {
            console.error(`Error patching event ${eventKey} in competition ${competition}:`, error);
            throw error;
        }
    }

    deleteEvent = async (eventKey, competition) => {
        if (!eventKey || !competition) {
            console.warn('deleteEvent called without eventKey or competition');
            return false;
        }
        try {
            const location = ref(this.db, `eventsNew/${competition}/${eventKey}`);
            await remove(location);
            console.log(`Deleted event ${eventKey} from competition ${competition}`);
        } catch (error) {
            console.error(`Error deleting event ${eventKey} from competition ${competition}:`, error);
            throw error;
        }
    }

    // --- Score Methods ---

    Score = (data) => ({
        comments: data.comments || '',
        finalScore: data.finalScore || '',
        delegate: data.delegate || {},
        delegateUid: data.delegateUid || '',
        event: data.event || {},
        eventUid: data.eventUid || '',
        judge: data.judge || {},
        judgeUid: data.judgeUid || '',
        team: data.team || {},
        teamUid: data.teamUid || '',
        penalties: data.penalties || {},
        quizQuestionsMissed: data.quizQuestionsMissed || 0,
        time: data.time || 0,
        minutes: data.minutes || 0,
        seconds: data.seconds || 0,
        createdAt: data.createdAt || this.now(),
        updatedAt: this.now(),
        scoreContested: data.scoreContested || false,
        reasonForChange: data.reasonForChange || '',
        additionalComments: data.additionalComments || '',
        excludeScore: data.excludeScore || false,
        scoreMax: data.scoreMax || false
    })

    insertScore = async (score, competition) => {
        const toSave = this.Score(score);

        // Remove unnecessary nested data
        toSave.event.judgesNew = null;
        toSave.event.associatedTeams = null;
        toSave.judge.eventsNew = null;
        toSave.judge.teamsNew = null;
        toSave.judge.role = null;
        toSave.judge.invited = null;
        toSave.team.associatedEvents = null;
        toSave.team.teamMembers = null;

        const location = ref(this.db, `scoresNew/${competition}`);
        const response = await push(location, toSave);
        console.log(`Inserted score with key ${response.key}`);
        return response.key;
    }

    patchScore = async (scoreKeyOrCompound, newScoreValue, competition) => {
        // Accept either ("competition/scoreKey", newScore) or (scoreKey, newScore, competition)
        let comp = competition;
        let key = scoreKeyOrCompound;
        if (!comp && typeof scoreKeyOrCompound === 'string' && scoreKeyOrCompound.includes('/')) {
            const parts = scoreKeyOrCompound.split('/');
            comp = parts[0];
            key = parts.slice(1).join('/');
        }
        if (!key || !comp) {
            console.warn('patchScore called without competition and scoreKey');
            return false;
        }
        const toSave = this.Score(newScoreValue);
        const location = ref(this.db, `scoresNew/${comp}/${key}`);
        await set(location, toSave);
        return true;
    }

    deleteScore = async (scoreKeyOrCompound, competition) => {
        // Accept either (scoreKey, competition) or ("competition/scoreKey")
        let comp = competition;
        let key = scoreKeyOrCompound;
        if (!comp && typeof scoreKeyOrCompound === 'string' && scoreKeyOrCompound.includes('/')) {
            const parts = scoreKeyOrCompound.split('/');
            comp = parts[0];
            key = parts.slice(1).join('/');
        }
        if (!key || !comp) {
            console.warn('deleteScore called without competition and scoreKey');
            return false;
        }
        try {
            const location = ref(this.db, `scoresNew/${comp}/${key}`);
            await remove(location);
            console.log(`Deleted score ${key} from competition ${comp}`);
            return true;
        } catch (error) {
            console.error(`Error deleting score ${key} from competition ${comp}:`, error);
            throw error;
        }
    }

    // Reads a single score by uid for a competition
    getScore = async (scoreUid, competition) => {
        if (!competition || !scoreUid) return {};
        const snap = await dbGet(ref(this.db, `scoresNew/${competition}/${scoreUid}`));
        return snap.val() || {};
    }

    // Reads all scores for a competition (legacy parity)
    getScores = async (competition) => {
        if (!competition) return {};
        const snap = await dbGet(ref(this.db, `scoresNew/${competition}`));
        return snap.val() || {};
    }

    // Filtered score queries (legacy parity)
    getScoresForEvent = async (eventUid, competition) => {
        if (!competition || !eventUid) return {};
        const q = dbQuery(ref(this.db, `scoresNew/${competition}`), orderByChild('eventUid'), equalTo(eventUid));
        const snap = await dbGet(q);
        return snap.val() || {};
    }

    getScoresForTeam = async (teamUid, competition) => {
        if (!competition || !teamUid) return {};
        const q = dbQuery(ref(this.db, `scoresNew/${competition}`), orderByChild('teamUid'), equalTo(teamUid));
        const snap = await dbGet(q);
        return snap.val() || {};
    }

    getScoresForJudge = async (judgeUid, competition) => {
        if (!competition || !judgeUid) return {};
        const q = dbQuery(ref(this.db, `scoresNew/${competition}`), orderByChild('judgeUid'), equalTo(judgeUid));
        const snap = await dbGet(q);
        return snap.val() || {};
    }

    // Past scores helpers
    insertPastScore = async (score, scoreUid, competition) => {
        if (!competition || !scoreUid) return null;
        const location = ref(this.db, `pastScoresNew/${competition}/${scoreUid}`);
        const res = await push(location, score);
        return res.key;
    }

    deletePastScore = async (scoreKeyOrCompound) => {
        // Legacy calls pass "competition/scoreUid"
        if (!scoreKeyOrCompound) return false;
        const location = ref(this.db, `pastScoresNew/${scoreKeyOrCompound}`);
        await remove(location);
        return true;
    }

    getPastScoresMap = async (scoreUid, competition) => {
        if (!competition || !scoreUid) return {};
        const snap = await dbGet(ref(this.db, `pastScoresNew/${competition}/${scoreUid}`));
        return snap.val() || {};
    }

    // Realtime scores root (for contexts and dashboards)
    listenScoresMap = () => ref(this.db, `scoresNew`);

    // Additional methods can be added here following the same pattern

    // --- Appeals Methods ---

    listenAppealsMap = () => ref(this.db, `appealsNew`);

    insertAppeal = async (appeal, competition) => {
        if (!competition) throw new Error('competition required');
        const now = this.now();

        // Prevent more than one OPEN appeal per team+event
        try {
            const existingSnap = await dbGet(ref(this.db, `appealsNew/${competition}`));
            const existing = existingSnap.val() || {};
            for (const key of Object.keys(existing)) {
                const a = existing[key];
                if (!a) continue;
                if (a.teamUid === (appeal.teamUid || '') && a.eventUid === (appeal.eventUid || '') && a.status === 'OPEN') {
                    throw new Error('An open appeal already exists for this team and event');
                }
            }
        } catch (e) {
            // If dbGet throws for missing path, continue; otherwise rethrow
            if (e.message && e.message.includes('An open appeal already exists')) throw e;
        }

        const toSave = {
            competition: competition,
            eventUid: appeal.eventUid || '',
            teamUid: appeal.teamUid || '',
            scoreUid: appeal.scoreUid || '',
            submitterUid: appeal.submitterUid || this.auth?.currentUser?.uid || '',
            submitterName: appeal.submitterName || '',
            reason: appeal.reason || '',
            details: appeal.details || '',
            evidence: appeal.evidence || [],
            status: appeal.status || 'OPEN', // OPEN | APPROVED | DENIED | REVOKED | CANCELLED
            createdAt: appeal.createdAt || now,
            updatedAt: this.now(),
            decision: appeal.decision || null,
            scoreChange: appeal.scoreChange || null,
            archived: !!appeal.archived
        };
        const location = ref(this.db, `appealsNew/${competition}`);
        const res = await push(location, toSave);
        const appealKey = res.key;
        // mark related score as contested if provided
        if (toSave.scoreUid) {
            try {
                await set(ref(this.db, `scoresNew/${competition}/${toSave.scoreUid}/scoreContested`), true);
            } catch (e) {
                console.warn('Failed to mark score as contested', e.message || e);
            }
        }
        return appealKey;
    }

    getAppeal = async (appealKeyOrCompound, competition) => {
        // Accept either (appealKey, competition) or ("competition/appealKey")
        let comp = competition;
        let key = appealKeyOrCompound;
        if (!comp && typeof appealKeyOrCompound === 'string' && appealKeyOrCompound.includes('/')) {
            const parts = appealKeyOrCompound.split('/');
            comp = parts[0];
            key = parts.slice(1).join('/');
        }
        if (!comp || !key) return null;
        const snap = await dbGet(ref(this.db, `appealsNew/${comp}/${key}`));
        return snap.exists() ? snap.val() : null;
    }

    getAppealsByCompetition = async (competition) => {
        if (!competition) return {};
        const snap = await dbGet(ref(this.db, `appealsNew/${competition}`));
        return snap.val() || {};
    }

    getAppealsForTeam = async (teamUid, competition) => {
        if (!competition || !teamUid) return {};
        const all = await this.getAppealsByCompetition(competition);
        const out = {};
        Object.keys(all).forEach(k => {
            const a = all[k];
            if (a && a.teamUid === teamUid) out[k] = a;
        });
        return out;
    }

    getAppealsForEvent = async (eventUid, competition) => {
        if (!competition || !eventUid) return {};
        const all = await this.getAppealsByCompetition(competition);
        const out = {};
        Object.keys(all).forEach(k => {
            const a = all[k];
            if (a && a.eventUid === eventUid) out[k] = a;
        });
        return out;
    }

    patchAppeal = async (appealKeyOrCompound, newAppealValue, competition) => {
        // Support either (appealKey, competition) or ("competition/appealKey")
        let comp = competition;
        let key = appealKeyOrCompound;
        if (!comp && typeof appealKeyOrCompound === 'string' && appealKeyOrCompound.includes('/')) {
            const parts = appealKeyOrCompound.split('/');
            comp = parts[0];
            key = parts.slice(1).join('/');
        }
        if (!comp || !key) {
            console.warn('patchAppeal called without competition and appealKey');
            return false;
        }

        // Load existing appeal to enforce permissions and field restrictions
        const existingSnap = await dbGet(ref(this.db, `appealsNew/${comp}/${key}`));
        if (!existingSnap.exists()) throw new Error('appeal not found');
        const existing = existingSnap.val();

        const currentUid = this.auth?.currentUser?.uid || '';

        // Only the submitter may edit an OPEN appeal and only specific fields
        const allowedWhenOpen = ['reason', 'details', 'evidence', 'submitterName'];
        if (existing.status === 'OPEN') {
            if (existing.submitterUid !== currentUid) throw new Error('Only the original submitter may edit an open appeal');
            const filtered = {};
            for (const f of allowedWhenOpen) {
                if (f in newAppealValue) filtered[f] = newAppealValue[f];
            }
            const toSave = Object.assign({}, existing, filtered, { updatedAt: this.now() });
            await set(ref(this.db, `appealsNew/${comp}/${key}`), toSave);
            return true;
        }

        // For non-OPEN appeals, prevent client-side arbitrary patching
        throw new Error('Appeal cannot be edited in its current state');
    }

    deleteAppeal = async (appealKeyOrCompound, competition) => {
        // Support either (appealKey, competition) or ("competition/appealKey")
        let comp = competition;
        let key = appealKeyOrCompound;
        if (!comp && typeof appealKeyOrCompound === 'string' && appealKeyOrCompound.includes('/')) {
            const parts = appealKeyOrCompound.split('/');
            comp = parts[0];
            key = parts.slice(1).join('/');
        }
        if (!comp || !key) throw new Error('competition and appeal key required');

        const appealSnap = await dbGet(ref(this.db, `appealsNew/${comp}/${key}`));
        if (!appealSnap.exists()) return false;
        const appeal = appealSnap.val();

        // Remove DB entry
        await set(ref(this.db, `appealsNew/${comp}/${key}`), null);

        // Unset contested flag on related score if present
        if (appeal && appeal.scoreUid) {
            try { await set(ref(this.db, `scoresNew/${comp}/${appeal.scoreUid}/scoreContested`), false); } catch (e) { /* ignore */ }
        }

        // Delete associated storage evidence folder if present
        try {
            const folderRef = storageRef(this.storage, `appeals/${comp}/${key}`);
            const listing = await listAll(folderRef);
            for (const item of listing.items) {
                try { await deleteObject(item); } catch (e) { console.warn('Failed deleting appeal evidence item', e.message || e); }
            }
        } catch (e) {
            // ignore: no folder or permissions
        }

        return true;
    }

    resolveAppeal = async (appealKeyOrCompound, resolution, competition) => {
        // resolution: { status: 'APPROVED'|'DENIED'|'REVOKED', byUid, byName, reason, newScore (optional) }
        let comp = competition;
        let key = appealKeyOrCompound;
        if (!comp && typeof appealKeyOrCompound === 'string' && appealKeyOrCompound.includes('/')) {
            const parts = appealKeyOrCompound.split('/');
            comp = parts[0];
            key = parts.slice(1).join('/');
        }
        if (!comp || !key) throw new Error('competition and appeal key required');

        const appealSnap = await dbGet(ref(this.db, `appealsNew/${comp}/${key}`));
        if (!appealSnap.exists()) throw new Error('appeal not found');
        const appeal = appealSnap.val();

        const now = this.now();
        const decision = {
            byUid: resolution.byUid || this.auth?.currentUser?.uid || '',
            byName: resolution.byName || this.auth?.currentUser?.displayName || this.auth?.currentUser?.email || '',
            reason: resolution.reason || '',
            status: resolution.status,
            decidedAt: now
        };

        // If we still don't have a byName but have a byUid, try to resolve the user's email/name from users/ DB
        if ((!decision.byName || decision.byName === '') && decision.byUid) {
            try {
                const userRecord = await this.getUser(decision.byUid);
                if (userRecord) {
                    decision.byName = userRecord.email || userRecord.displayName || userRecord.name || '';
                }
            } catch (e) {
                // ignore failures to lookup
            }
        }

        // If the resolution is a revoke and it's requested by the submitter, fully delete the appeal
        if (resolution.status === 'REVOKED') {
            const requesterUid = resolution.byUid || this.auth?.currentUser?.uid || '';
            if (requesterUid && appeal.submitterUid && requesterUid === appeal.submitterUid) {
                // delete the appeal entirely
                try {
                    await this.deleteAppeal(key, comp);
                    return true;
                } catch (e) {
                    console.error('Failed deleting appeal on revoke', e.message || e);
                    throw e;
                }
            }
            // Otherwise fall through to record the revoked status
        }

        // If approved and newScore provided, apply score change
        let scoreChange = null;
        if (resolution.status === 'APPROVED' && resolution.newScore && appeal.scoreUid) {
            try {
                const currentScore = await this.getScore(appeal.scoreUid, comp);
                // Save current to pastScores
                await this.insertPastScore(currentScore, appeal.scoreUid, comp);
                // Apply new score - caller should pass full score object; preserve some metadata
                await this.patchScore(appeal.scoreUid, resolution.newScore, comp);
                scoreChange = {
                    oldScore: currentScore.finalScore || null,
                    newScore: resolution.newScore.finalScore || null,
                    appliedByUid: decision.byUid,
                    appliedAt: now
                };
                // mark as not contested anymore
                try { await set(ref(this.db, `scoresNew/${comp}/${appeal.scoreUid}/scoreContested`), false); } catch (e) { /* ignore */ }
            } catch (e) {
                console.error('Failed applying approved score change', e.message || e);
                throw e;
            }
        }

        const updatedAppeal = Object.assign({}, appeal, {
            status: resolution.status,
            decision,
            scoreChange,
            updatedAt: now
        });

        await set(ref(this.db, `appealsNew/${comp}/${key}`), updatedAppeal);
        return true;
    }

    reopenAppeal = async (appealKeyOrCompound, competition) => {
        // Support either (appealKey, competition) or ("competition/appealKey")
        let comp = competition;
        let key = appealKeyOrCompound;
        if (!comp && typeof appealKeyOrCompound === 'string' && appealKeyOrCompound.includes('/')) {
            const parts = appealKeyOrCompound.split('/');
            comp = parts[0];
            key = parts.slice(1).join('/');
        }
        if (!comp || !key) throw new Error('competition and appeal key required');

        const appealSnap = await dbGet(ref(this.db, `appealsNew/${comp}/${key}`));
        if (!appealSnap.exists()) throw new Error('appeal not found');
        const appeal = appealSnap.val();

        // Only allow reopen if currently APPROVED or DENIED
        if (!appeal || (appeal.status !== 'APPROVED' && appeal.status !== 'DENIED')) {
            throw new Error('Only approved or denied appeals may be reopened');
        }

        const now = this.now();
        const updated = Object.assign({}, appeal, {
            status: 'OPEN',
            decision: null,
            scoreChange: null,
            updatedAt: now
        });

        await set(ref(this.db, `appealsNew/${comp}/${key}`), updated);

        // Mark related score as contested again if present
        if (appeal.scoreUid) {
            try { await set(ref(this.db, `scoresNew/${comp}/${appeal.scoreUid}/scoreContested`), true); } catch (e) { /* ignore */ }
        }

        return true;
    }

    // --- Appeals Storage Helpers ---

    storageUploadAppealEvidence = async (file, fileName, competition, appealId) => {
        if (!file || !competition || !appealId || !fileName) throw new Error('file, competition and appealId required');
        try {
            const path = `appeals/${competition}/${appealId}/${fileName}`;
            const reference = storageRef(this.storage, path);
            const snapshot = await uploadBytes(reference, file);
            const url = await getDownloadURL(snapshot.ref);
            return { name: fileName, path, url };
        } catch (error) {
            console.error('storageUploadAppealEvidence error', error);
            throw error;
        }
    }

    // Helper to delete all evidence for an appeal
    storageDeleteAppealEvidence = async (competition, appealId) => {
        if (!competition || !appealId) throw new Error('competition and appealId required');
        try {
            const folderRef = storageRef(this.storage, `appeals/${competition}/${appealId}`);
            const listing = await listAll(folderRef);
            for (const item of listing.items) {
                try { await deleteObject(item); } catch (e) { console.warn('Failed deleting appeal evidence item', e.message || e); }
            }
            return true;
        } catch (e) {
            // ignore if no folder
            return false;
        }
    }


    // Scoresheets helpers (global and per-event)
    listenScoresheetsMap = () => ref(this.db, 'scoresheets');

    insertGlobalScoresheet = async (values, competition) => {
        if (!competition) throw new Error('competition required');
        const location = ref(this.db, `scoresheets/${competition}/global`);
        return set(location, values);
    }

    editGlobalScoresheet = async (values, competition) => {
        if (!competition) throw new Error('competition required');
        const location = ref(this.db, `scoresheets/${competition}/global`);
        return set(location, values);
    }

    insertEventScoresheet = async (values, eventUid, competition) => {
        if (!competition || !eventUid) throw new Error('competition and eventUid required');
        const location = ref(this.db, `scoresheets/${competition}/events/${eventUid}`);
        return set(location, values);
    }

    editEventScoresheet = async (values, eventUid, competition) => {
        if (!competition || !eventUid) throw new Error('competition and eventUid required');
        const location = ref(this.db, `scoresheets/${competition}/events/${eventUid}`);
        return set(location, values);
    }

    deleteEventScoresheet = async (eventUid, competition) => {
        if (!eventUid || !competition) return false;
        const location = ref(this.db, `scoresheets/${competition}/events/${eventUid}`);
        await remove(location);
        return true;
    }

    getScoresheetsByCompetition = async (competition) => {
        if (!competition) return {};
        const snap = await dbGet(ref(this.db, `scoresheets/${competition}`));
        return snap.val() || {};
    }

    // Calculate percentage of expected scores that have been entered for a competition
    getPercentageScoresDone = async (competition) => {
        if (!competition) return null;
        try {
            const [eventsSnap, teamsSnap, scoresSnap] = await Promise.all([
                dbGet(ref(this.db, `eventsNew/${competition}`)),
                dbGet(ref(this.db, `teamsNew/${competition}`)),
                dbGet(ref(this.db, `scoresNew/${competition}`))
            ]);

            const events = eventsSnap.val() || {};
            const teams = teamsSnap.val() || {};
            const scores = scoresSnap.val() || {};

            const teamCount = Object.keys(teams).length;

            // Compute expected event-team pairs. If event has associatedTeams, use that count; otherwise assume all teams.
            let expected = 0;
            Object.keys(events).forEach((eK) => {
                const ev = events[eK] || {};
                const assoc = ev.associatedTeams || [];
                if (Array.isArray(assoc) && assoc.length > 0) expected += assoc.length;
                else if (assoc && typeof assoc === 'object') expected += Object.keys(assoc).length;
                else expected += teamCount;
            });

            if (expected === 0) return null;

            // Count unique recorded event-team pairs from scores (ignore excluded scores)
            // Only count a pair if the stored score is a numeric value > 0
            const seen = new Set();
            Object.keys(scores).forEach((sK) => {
                const s = scores[sK] || {};
                if (s.excludeScore) return;
                const e = s.eventUid || s.event?.eventUid;
                const t = s.teamUid || s.team?.teamUid;
                if (!e || !t) return;

                // Determine final score from possible shapes the score object may take
                const rawScore = (s.finalScore !== undefined && s.finalScore !== null) ? s.finalScore : (s.score && s.score.finalScore !== undefined ? s.score.finalScore : null);
                const num = rawScore === null || rawScore === '' ? NaN : Number(rawScore);
                if (!Number.isFinite(num) || num <= 0) return; // only count positive scores

                seen.add(`${e}::${t}`);
            });

            const recorded = seen.size;
            const ratio = Math.max(0, Math.min(1, recorded / expected));
            return ratio;
        } catch (err) {
            console.error('getPercentageScoresDone error', err);
            return null;
        }
    }

    // --- Liability Methods ---

    listenLiabilityMap = () => ref(this.db, 'liabilityForm');

    upsertLiabiltyFormDescription = async (competition, value) => {
        const location = ref(this.db, `liabilityForm/${competition}/details`);
        return set(location, value);
    }

    insertLiabilityForm = async (competition, agreedToConsentSelf, agreedToConsentSelfMinor) => {
        const uid = this.auth?.currentUser?.uid;
        if (!uid) throw new Error('No authenticated user');
        const location = ref(this.db, `liabilityForm/${competition}/signatories/${uid}`);
        return set(location, {
            agreedToConsentSelf: agreedToConsentSelf === '' ? null : agreedToConsentSelf,
            agreedToConsentSelfMinor: agreedToConsentSelfMinor === '' ? null : agreedToConsentSelfMinor,
            createdAt: Timestamp.now().toMillis()
        });
    }

    getLiabilityFormSignatories = async (competition) => {
        const uid = this.auth?.currentUser?.uid;
        if (!uid) return false;
        const form = await dbGet(ref(this.db, `liabilityForm/${competition}/signatories/${uid}`));
        return form.val() || false;
    }

    resetLiabilityFormEntries = async (competition) => {
        const location = ref(this.db, `liabilityForm/${competition}/signatories`);
        return set(location, null);
    }

    // --- Registration Settings / Registration Methods ---

    listenRegistrationSettings = () => ref(this.db, 'registrationSettings');

    getRegistrationSettings = async () => {
        const snap = await dbGet(ref(this.db, 'registrationSettings'));
        return snap.val() || {};
    }

    setTeamRegistrationSettings = async (competition, values) => {
        const location = ref(this.db, `registrationSettings/${competition}/team`);
        return set(location, values);
    }

    setEventRegistrationSettings = async (competition, values) => {
        const location = ref(this.db, `registrationSettings/${competition}/event`);
        return set(location, values);
    }

    setSponsorRegistrationSettings = async (competition, values) => {
        const location = ref(this.db, `registrationSettings/${competition}/sponsor`);
        return set(location, values);
    }

    // Sponsor registration
    insertSponsorRegistration = async (competition, values) => {
        const location = ref(this.db, `registration/${competition}/sponsor`);
        const result = await push(location, values);
        return result.key;
    }

    patchSponsorRegistration = async (competition, id, values) => {
        const location = ref(this.db, `registration/${competition}/sponsor/${id}`);
        return set(location, values);
    }

    getSponsorRegistration = async (competition, id) => {
        const snap = await dbGet(ref(this.db, `registration/${competition}/sponsor/${id}`));
        return snap.val() || {};
    }

    setSponsorRegistrationPushed = async (competition, pushed) => {
        const location = ref(this.db, `registration/${competition}/settings/sponsor/pushed`);
        return set(location, pushed);
    }

    getSponsorRegistrationPushed = async (competition) => {
        const snap = await dbGet(ref(this.db, `registration/${competition}/settings/sponsor/pushed`));
        return snap.val();
    }

    getSponsorRegistrations = async (competition) => {
        const snap = await dbGet(ref(this.db, `registration/${competition}/sponsor`));
        return snap.val() || {};
    }

    deleteSponsorRegistration = async (competition, id) => {
        const location = ref(this.db, `registration/${competition}/sponsor/${id}`);
        await remove(location);
        return true;
    }

    // Event registration
    insertEventRegistration = async (competition, values) => {
        const location = ref(this.db, `registration/${competition}/event`);
        const result = await push(location, values);
        return result.key;
    }

    patchEventRegistration = async (competition, id, values) => {
        const location = ref(this.db, `registration/${competition}/event/${id}`);
        return set(location, values);
    }

    getEventRegistration = async (competition, id) => {
        const snap = await dbGet(ref(this.db, `registration/${competition}/event/${id}`));
        return snap.val() || {};
    }

    setEventRegistrationPushed = async (competition, pushed) => {
        const location = ref(this.db, `registration/${competition}/settings/event/pushed`);
        return set(location, pushed);
    }

    getEventRegistrationPushed = async (competition) => {
        const snap = await dbGet(ref(this.db, `registration/${competition}/settings/event/pushed`));
        return snap.val();
    }

    getEventRegistrations = async (competition) => {
        const snap = await dbGet(ref(this.db, `registration/${competition}/event`));
        return snap.val() || {};
    }

    deleteEventRegistration = async (competition, id) => {
        const location = ref(this.db, `registration/${competition}/event/${id}`);
        await remove(location);
        return true;
    }

    // Registration storage helpers
    storageGetUserEventFile = async (fileName, competition, id) => {
        try {
            const reference = storageRef(this.storage, `registration/${competition}/event/${id}/${fileName}`);
            const url = await getDownloadURL(reference);
            return url;
        } catch (e) {
            return null;
        }
    }

    storageUploadUserEventRegistrationFile = async (file, fileName, competition, id) => {
        const reference = storageRef(this.storage, `registration/${competition}/event/${id}/${fileName}`);
        const result = await uploadBytes(reference, file);
        return result;
    }

    storageGetUserEventRegistrationFile = async (fileName, competition, id) => {
        try {
            const reference = storageRef(this.storage, `registrationSettings/${competition}/event/${id}/${fileName}`);
            const url = await getDownloadURL(reference);
            return url;
        } catch (e) {
            return null;
        }
    }

    // --- Global Settings Methods ---

    listenGlobalSettings = () => ref(this.db, 'globalSettings');

    getGlobalSettings = async () => {
        const snap = await dbGet(ref(this.db, 'globalSettings'));
        return snap.val() || {};
    }

    getShowFinalScoresToTeams = async (competition) => {
        const snap = await dbGet(ref(this.db, `globalSettings/showFinalScoresToTeams/${competition}`));
        return snap.val() || {};
    }

    setShowFinalScoresToTeams = async (competition, value) => {
        const location = ref(this.db, `globalSettings/showFinalScoresToTeams/${competition}`);
        return set(location, value);
    }

    getLockCompetition = async (competition) => {
        const snap = await dbGet(ref(this.db, `globalSettings/lockCompetition/${competition}`));
        return snap.val() || {};
    }

    setLockCompetition = async (competition, value) => {
        const location = ref(this.db, `globalSettings/lockCompetition/${competition}`);
        return set(location, value);
    }

    getDivisionMap = async () => {
        const snap = await dbGet(ref(this.db, `globalSettings/divisions`));
        return snap.val() || {};
    }

    getDivisionMapByCompetition = async (competition) => {
        const snap = await dbGet(ref(this.db, `globalSettings/divisions/${competition}`));
        return snap.val() || {};
    }

    insertDivision = async (division, competition) => {
        const location = ref(this.db, `globalSettings/divisions/${competition}`);
        return push(location, division);
    }

    patchDivision = async (divisionKey, newDivisionValue) => {
        const location = ref(this.db, `globalSettings/divisions/${divisionKey}`);
        return set(location, newDivisionValue);
    }

    deleteDivision = async (divisionKey, competition) => {
        if (!divisionKey || !competition) return false;
        const location = ref(this.db, `globalSettings/divisions/${competition}/${divisionKey}`);
        await remove(location);
        return true;
    }

    getCategoryMap = async () => {
        const snap = await dbGet(ref(this.db, `globalSettings/categories`));
        return snap.val() || {};
    }

    insertCategory = async (category, competition) => {
        const location = ref(this.db, `globalSettings/categories/${competition}`);
        return push(location, category);
    }

    patchCategory = async (categoryKey, newCategoryValue) => {
        const location = ref(this.db, `globalSettings/categories/${categoryKey}`);
        return set(location, newCategoryValue);
    }

    deleteCategory = async (categoryKey, competition) => {
        if (!categoryKey || !competition) return false;
        const location = ref(this.db, `globalSettings/categories/${competition}/${categoryKey}`);
        await remove(location);
        return true;
    }

    getDefaultCompetition = async () => {
        const snap = await dbGet(ref(this.db, `globalSettings/defaultCompetition`));
        return snap.val() || '';
    }

    setDefaultCompetition = async (key) => {
        const location = ref(this.db, `globalSettings/defaultCompetition`);
        return set(location, key);
    }

    setUserDefaultCompetition = async (uid, key) => {
        return set(ref(this.db, `users/${uid}/defaultCompetition`), key);
    }

    getCompetitionMap = async () => {
        const snap = await dbGet(ref(this.db, `globalSettings/competitions`));
        return snap.val() || {};
    }

    insertCompetition = async (competition) => {
        const location = ref(this.db, `globalSettings/competitions`);
        return push(location, competition);
    }

    patchCompetition = async (competitionKey, newCompetitionValue) => {
        const location = ref(this.db, `globalSettings/competitions/${competitionKey}`);
        return set(location, newCompetitionValue);
    }

    deleteCompetition = async (competitionKey) => {
        if (!competitionKey) return false;
        const location = ref(this.db, `globalSettings/competitions/${competitionKey}`);
        await remove(location);
        return true;
    }

    // COMPLEX: delete all data associated with a competition
    deleteCompetitionComplex = async (competitionKey) => {
        if (!competitionKey) return false;
        try {
            // Clear common database paths for the competition
            const pathsToRemove = [
                `eventsNew/${competitionKey}`,
                `teamsNew/${competitionKey}`,
                `scoresNew/${competitionKey}`,
                `pastScoresNew/${competitionKey}`,
                `schedule/competitions/${competitionKey}`,
                `registration/${competitionKey}`,
                `registrationSettings/${competitionKey}`,
                `liabilityForm/${competitionKey}`,
                `scoresheets/${competitionKey}`,
                `globalSettings/divisions/${competitionKey}`,
                `globalSettings/categories/${competitionKey}`,
                `globalSettings/showFinalScoresToTeams/${competitionKey}`,
                `globalSettings/lockCompetition/${competitionKey}`,
                `globalSettings/competitions/${competitionKey}`
            ];

            for (const p of pathsToRemove) {
                try {
                    await remove(ref(this.db, p));
                    console.log(`Removed path ${p}`);
                } catch (e) {
                    console.warn(`Failed removing path ${p}:`, e.message || e);
                }
            }

            // If global defaultCompetition points to this competition, clear it
            try {
                const defaultSnap = await dbGet(ref(this.db, `globalSettings/defaultCompetition`));
                if (defaultSnap.exists() && defaultSnap.val() === competitionKey) {
                    await set(ref(this.db, `globalSettings/defaultCompetition`), '');
                    console.log('Cleared global defaultCompetition');
                }
            } catch (e) {
                console.warn('Error checking/clearing defaultCompetition', e.message || e);
            }

            // Cleanup presentation-music storage folder (list+delete)
            try {
                const musicRef = storageRef(this.storage, `presentation-music/${competitionKey}`);
                const musicList = await listAll(musicRef);
                for (const item of musicList.items) {
                    try { await deleteObject(item); console.log(`Deleted storage item ${item.fullPath}`); } catch (e) { console.warn('Failed deleting presentation music item', e.message || e); }
                }
            } catch (e) {
                console.warn('No presentation-music folder or failed to list it', e.message || e);
            }

            // Cleanup registrationSettings files stored in storage
            try {
                const regRef = storageRef(this.storage, `registrationSettings/${competitionKey}`);
                const regList = await listAll(regRef);
                for (const item of regList.items) {
                    try { await deleteObject(item); console.log(`Deleted storage item ${item.fullPath}`); } catch (e) { console.warn('Failed deleting registrationSettings item', e.message || e); }
                }
            } catch (e) {
                console.warn('No registrationSettings storage folder or failed to list it', e.message || e);
            }

            // Cleanup liability form files in storage
            try {
                const libRef = storageRef(this.storage, `liabilityForm/${competitionKey}`);
                const libList = await listAll(libRef);
                for (const item of libList.items) {
                    try { await deleteObject(item); console.log(`Deleted storage item ${item.fullPath}`); } catch (e) { console.warn('Failed deleting liabilityForm item', e.message || e); }
                }
            } catch (e) {
                console.warn('No liabilityForm storage folder or failed to list it', e.message || e);
            }

            // Remove references from users: competitions, eventsNew, teamsNew and clear defaultCompetition if set
            try {
                const usersSnap = await dbGet(ref(this.db, 'users'));
                const users = usersSnap.val() || {};
                for (const uid of Object.keys(users)) {
                    try {
                        await remove(ref(this.db, `users/${uid}/competitions/${competitionKey}`));
                        await remove(ref(this.db, `users/${uid}/eventsNew/${competitionKey}`));
                        await remove(ref(this.db, `users/${uid}/teamsNew/${competitionKey}`));
                        const userDefaultSnap = await dbGet(ref(this.db, `users/${uid}/defaultCompetition`));
                        if (userDefaultSnap.exists() && userDefaultSnap.val() === competitionKey) {
                            await set(ref(this.db, `users/${uid}/defaultCompetition`), '');
                        }
                    } catch (e) {
                        console.warn(`Failed cleaning user ${uid}:`, e.message || e);
                    }
                }
            } catch (e) {
                console.warn('Failed iterating users for cleanup', e.message || e);
            }

            console.log(`Completed complex deletion for competition ${competitionKey}`);
            return true;
        } catch (error) {
            console.error('deleteCompetitionComplex error', error);
            throw error;
        }
    }

    // --- Schedule / Slideshow Methods ---

    insertSchedule = async (currentCompetitionKey, data) => {
        const location = ref(this.db, `schedule/competitions/${currentCompetitionKey}`);
        try {
            // Save the array directly to preserve array structure in the Realtime Database
            await set(location, data);
            console.log(`Data saved to schedule/competitions/${currentCompetitionKey}`);
            return true;
        } catch (error) {
            console.error('Error saving schedule:', error);
            throw error;
        }
    };

    getSchedule = async (currentCompetitionKey) => {
        const location = ref(this.db, `schedule/competitions/${currentCompetitionKey}`);
        try {
            const snapshot = await dbGet(location);
            if (snapshot.exists()) {
                let schedule = snapshot.val();
                // Normalize to array if stored as an object (legacy)
                if (!Array.isArray(schedule) && schedule && typeof schedule === 'object') {
                    const arr = Object.keys(schedule)
                        .filter(k => !isNaN(Number(k)))
                        .sort((a, b) => Number(a) - Number(b))
                        .map(k => schedule[k]);
                    schedule = arr;
                }
                return schedule;
            } else {
                return null;
            }
        } catch (error) {
            console.error('Error retrieving schedule:', error);
            return null;
        }
    };

    getImage = async (competition, imageName) => {
        try {
            const reference = storageRef(this.storage, `schedule/${competition}/${imageName}`);
            const url = await getDownloadURL(reference);
            return url;
        } catch (e) {
            return null;
        }
    };

    getImages = async (competition) => {
        const filesRef = storageRef(this.storage, `schedule/${competition}`);
        try {
            const res = await listAll(filesRef);
            const fileNames = res.items.map((itemRef) => itemRef.name);
            return fileNames;
        } catch (error) {
            return [];
        }
    }

    uploadImage = async (competition, file) => {
        if (!file) {
            console.error('No file provided for upload.');
            return null;
        }
        const fileRef = storageRef(this.storage, `schedule/${competition}/${file.name}`);
        try {
            const snapshot = await uploadBytes(fileRef, file);
            const downloadURL = await getDownloadURL(snapshot.ref);
            return downloadURL;
        } catch (error) {
            console.error('Error uploading file:', error);
            return null;
        }
    }

    // --- Storage Methods ---

    storageUploadLiabilityForm = async (file, fileName, competition) => {
        try {
            const reference = storageRef(this.storage, `liabilityForm/${competition}/${fileName}`);
            const result = await uploadBytes(reference, file);
            console.log(`Uploaded liability form file ${fileName} for competition ${competition}`);
            return result;
        } catch (error) {
            console.error(`Error uploading liability form file ${fileName} for competition ${competition}:`, error);
            throw error;
        }
    }

    storageGetLiabilityForm = async (fileName, competition) => {
        try {
            const reference = storageRef(this.storage, `liabilityForm/${competition}/${fileName}`);
            const url = await getDownloadURL(reference);
            console.log(`Retrieved liability form file URL ${fileName} for competition ${competition}`);
            return url;
        } catch (error) {
            console.error(`Error retrieving liability form file ${fileName} for competition ${competition}:`, error);
            return null;
        }
    }

    storageUploadRegistrationFile = async (file, fileName, competition) => {
        try {
            const reference = storageRef(this.storage, `registrationSettings/${competition}/${fileName}`);
            const result = await uploadBytes(reference, file);
            console.log(`Uploaded registration file ${fileName} for competition ${competition}`);
            return result;
        } catch (error) {
            console.error(`Error uploading registration file ${fileName} for competition ${competition}:`, error);
            throw error;
        }
    }

    storageGetRegistrationFile = async (fileName, competition) => {
        try {
            const reference = storageRef(this.storage, `registrationSettings/${competition}/${fileName}`);
            const url = await getDownloadURL(reference);
            console.log(`Retrieved registration file URL ${fileName} for competition ${competition}`);
            return url;
        } catch (error) {
            console.error(`Error retrieving registration file ${fileName} for competition ${competition}:`, error);
            return null;
        }
    }

    // Additional storage-related methods can be added here



    // Presentation music helpers (global by default)

    // --- Appeals Storage Helpers ---

    storageUploadAppealEvidence = async (file, fileName, competition, appealId) => {
        if (!file || !competition || !appealId || !fileName) throw new Error('file, competition and appealId required');
        try {
            const path = `appeals/${competition}/${appealId}/${fileName}`;
            const reference = storageRef(this.storage, path);
            const snapshot = await uploadBytes(reference, file);
            const url = await getDownloadURL(snapshot.ref);
            return { name: fileName, path, url };
        } catch (error) {
            console.error('storageUploadAppealEvidence error', error);
            throw error;
        }
    }



    getPresentationMusicList = async (competition = 'global') => {
        try {
            const dir = `presentation-music/${competition}`;
            const filesRef = storageRef(this.storage, dir);
            const res = await listAll(filesRef);
            const items = await Promise.all(res.items.map(async (itemRef) => {
                try {
                    const url = await getDownloadURL(itemRef);
                    return { name: itemRef.name, path: `${dir}/${itemRef.name}`, url };
                } catch (e) {
                    return { name: itemRef.name, path: `${dir}/${itemRef.name}`, url: null };
                }
            }));
            return items;
        } catch (error) {
            console.error('getPresentationMusicList error', error);
            return [];
        }
    }

    getPresentationMusicUrl = async (fileName, competition = 'global') => {
        try {
            const path = `presentation-music/${competition}/${fileName}`;
            const reference = storageRef(this.storage, path);
            const url = await getDownloadURL(reference);
            return url;
        } catch (error) {
            console.error(`Error getting presentation music url for ${fileName}`, error);
            return null;
        }
    }

    uploadPresentationMusic = async (file, competition = 'global') => {
        if (!file) {
            throw new Error('No file provided');
        }
        try {
            const path = `presentation-music/${competition}/${file.name}`;
            const reference = storageRef(this.storage, path);
            const snapshot = await uploadBytes(reference, file);
            const url = await getDownloadURL(snapshot.ref);
            console.log(`Uploaded presentation music ${file.name} to ${path}`);
            return { name: file.name, path, url };
        } catch (error) {
            console.error('uploadPresentationMusic error', error);
            throw error;
        }
    }

    deletePresentationMusic = async (fileName, competition = 'global') => {
        try {
            const path = `presentation-music/${competition}/${fileName}`;
            const reference = storageRef(this.storage, path);
            await deleteObject(reference);
            console.log(`Deleted presentation music ${fileName} from ${path}`);
            return true;
        } catch (error) {
            console.error(`Error deleting presentation music ${fileName}`, error);
            return false;
        }
    }


    // Placeholder for other methods copied from FirebaseNew.js with improvements to be added incrementally

}
